import React, { useEffect, useMemo, useState } from "react";
import { MdFlag, MdDelete, MdAdd } from "react-icons/md";
import { toast } from "react-toastify";
import Card from "../../../../components/card";
import { Goal, GoalType } from "../../../../interfaces/Goal";
import { createGoal, deleteGoal, getGoals } from "../../../../services/goalApi";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";

/**
 * Cadastro de metas da empresa. Vive dentro de Configurações (e não numa rota própria) porque meta
 * é configuração, não cadastro operacional — e a sidebar já tem 20+ itens.
 *
 * Não há edição: sobrescrever uma meta é lançar uma vigência nova. O histórico da decisão fica
 * preservado, que é o ponto de guardar `notes`.
 */

const TYPES: { key: GoalType; title: string; desc: string; unit: string }[] = [
  {
    key: "Revenue",
    title: "Faturamento mensal",
    desc: "Meta de receita do mês. O KPI da home mostra o realizado como percentual dela.",
    unit: "R$",
  },
  {
    key: "Utilization",
    title: "Utilização do time",
    desc: "Meta de horas planejadas ÷ capacidade. Incide sobre a utilização planejada — é o que o time controla ao planejar a semana.",
    unit: "%",
  },
];

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtValue = (g: Goal) =>
  g.type === "Revenue" ? fmtMoney(g.targetValue) : `${g.targetValue.toLocaleString("pt-BR")}%`;

/** "01/08/2026" → "ago/2026". A vigência é sempre o 1º dia do mês; o dia só polui. */
const fmtMonth = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", { month: "short", year: "numeric" });

/** Mês corrente no formato do <input type="month">. */
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const GoalsSettings: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [type, setType] = useState<GoalType>("Revenue");
  const [month, setMonth] = useState(currentMonth());
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");

  const load = () => {
    setLoading(true);
    getGoals()
      .then(setGoals)
      .catch((e) => console.error("Erro ao carregar metas", e))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const selectedType = TYPES.find((t) => t.key === type)!;

  const currentByType = useMemo(() => {
    const map: Partial<Record<GoalType, Goal>> = {};
    goals.forEach((g) => {
      if (g.isCurrent) map[g.type] = g;
    });
    return map;
  }, [goals]);

  const save = async () => {
    const parsed = Number(value.replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Informe um valor maior que zero.");
      return;
    }
    if (type === "Utilization" && parsed > 100) {
      toast.error("A meta de utilização é um percentual e não pode passar de 100.");
      return;
    }

    setSaving(true);
    try {
      await createGoal({
        type,
        effectiveFrom: `${month}-01`,
        targetValue: parsed,
        notes: notes.trim() || null,
      });
      toast.success("Meta cadastrada.");
      setValue("");
      setNotes("");
      load();
    } catch (e: any) {
      console.error("Erro ao salvar meta", e);
      toast.error(mensagemDoServidor(e, "Erro ao salvar meta."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (goal: Goal) => {
    if (
      !window.confirm(
        `Excluir a meta de ${fmtValue(goal)} a partir de ${fmtMonth(goal.effectiveFrom)}? A meta anterior volta a valer.`
      )
    )
      return;
    try {
      await deleteGoal(goal.id);
      toast.success("Meta excluída.");
      load();
    } catch (e) {
      console.error("Erro ao excluir meta", e);
      toast.error("Erro ao excluir meta.");
    }
  };

  return (
    <Card extra="p-5">
      <div className="mb-1 flex items-center gap-2">
        <MdFlag className="h-5 w-5 text-dv-blue-500 dark:text-dv-cyan-300" aria-hidden="true" />
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">Metas</h3>
      </div>
      <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
        Uma meta vale a partir do mês informado e continua valendo até que outra a substitua — meta
        lançada em janeiro cobre o ano inteiro. Sem meta cadastrada, os indicadores comparam com o
        mês anterior em vez do objetivo.
      </p>

      {/* Vigentes */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {TYPES.map((t) => {
          const current = currentByType[t.key];
          return (
            <div
              key={t.key}
              className="rounded-2xl border border-gray-100 p-4 dark:border-white/10"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                {t.title}
              </p>
              <p className="mt-1 font-display text-2xl font-bold tabular-nums text-navy-700 dark:text-white">
                {current ? fmtValue(current) : "Sem meta"}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                {current ? `Vigente desde ${fmtMonth(current.effectiveFrom)}` : t.desc}
              </p>
            </div>
          );
        })}
      </div>

      {/* Nova meta */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor="goal-type" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
            Tipo
          </label>
          <select
            id="goal-type"
            className={inputCls}
            value={type}
            onChange={(e) => setType(e.target.value as GoalType)}
          >
            {TYPES.map((t) => (
              <option key={t.key} value={t.key}>
                {t.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="goal-month" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
            Vale a partir de
          </label>
          <input
            id="goal-month"
            type="month"
            className={inputCls}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="goal-value" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
            Valor ({selectedType.unit})
          </label>
          <input
            id="goal-value"
            type="text"
            inputMode="decimal"
            placeholder={type === "Revenue" ? "220000" : "85"}
            className={inputCls}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="goal-notes" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
            Motivo (opcional)
          </label>
          <input
            id="goal-notes"
            type="text"
            placeholder="Por que a meta é esta"
            className={inputCls}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="flex items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
        >
          <MdAdd className="h-4 w-4" aria-hidden="true" />
          {saving ? "Salvando…" : "Cadastrar meta"}
        </button>
      </div>

      {/* Histórico */}
      <div className="mt-5 overflow-x-auto rounded-2xl border border-gray-100 dark:border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-navy-900">
              {["Tipo", "Vale a partir de", "Valor", "Motivo", ""].map((h, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-700 dark:text-gray-300">
                  Carregando…
                </td>
              </tr>
            ) : goals.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-700 dark:text-gray-300">
                  Nenhuma meta cadastrada.
                </td>
              </tr>
            ) : (
              goals.map((g) => (
                <tr
                  key={g.id}
                  className="border-b border-gray-100 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-navy-700"
                >
                  <td className="px-4 py-2.5">
                    {TYPES.find((t) => t.key === g.type)?.title ?? g.type}
                    {g.isCurrent && (
                      <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        Vigente
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums">{fmtMonth(g.effectiveFrom)}</td>
                  <td className="px-4 py-2.5 font-semibold tabular-nums text-navy-700 dark:text-white">
                    {fmtValue(g)}
                  </td>
                  <td className="px-4 py-2.5">{g.notes ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => remove(g)}
                      aria-label="Excluir meta"
                      className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                    >
                      <MdDelete className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default GoalsSettings;
